import { useEffect, useRef } from "react";

interface PlatformSource {
    platform?: string;
    userAgentData?: { platform?: string };
}

/** Whether the device is Apple's, where ⌘ is the shortcut key and Ctrl+K deletes to the end of a line. */
export function isApplePlatform(source: PlatformSource | undefined = globalThis.navigator as PlatformSource | undefined): boolean {
    const platform = source?.userAgentData?.platform || source?.platform || "";
    return /mac|iphone|ipad/i.test(platform);
}

/** ⌘K on Apple's devices and Ctrl+K elsewhere, with neither Alt nor Shift, which other shortcuts keep for themselves. */
function isPaletteShortcut(event: KeyboardEvent): boolean {
    const modifier = isApplePlatform() ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
    return (
        modifier &&
        !event.altKey &&
        !event.shiftKey &&
        typeof event.key === "string" &&
        event.key.toLowerCase() === "k"
    );
}

/** Opens and shuts the quick-jump palette from anywhere in the window. */
export function useCommandPaletteShortcut(onToggle: () => void): void {
    const latest = useRef(onToggle);
    useEffect(() => {
        latest.current = onToggle;
    }, [onToggle]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.isComposing || !isPaletteShortcut(event)) return;
            event.preventDefault();
            if (!event.repeat) latest.current();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);
}

/** How the shortcut is written for this device: "⌘K" on Apple's, "Ctrl+K" elsewhere. */
export function shortcutLabel(source: PlatformSource | undefined = globalThis.navigator as PlatformSource | undefined): string {
    return isApplePlatform(source) ? "⌘K" : "Ctrl+K";
}
